import {
  convert_char,
  convert_types,
  utf_code,
  assist_digit,
  textarea_default_row,
  fm_types,
} from '../Const';
import { Content } from '../types/Template';

const NUMBER_DECIMALS = 3; // 小数点以下桁数
const NUMBER_DECIMALS_ROUND_ZERO = 2; // 小数点第3位が0で丸める必要がある場合
const FIXED_NUMBER = 2; //convertToDecimalCommaで、小数点の切り捨て位置

/**
 * 全角数字→半角数字変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertFullNumericToHalfNumeric = (original: string): string => {
  let result = '';

  if (typeof original !== 'string' || original == null) {
    return '';
  }

  for (let i = 0; i < original.length; i++) {
    const code_original = original.charCodeAt(i);

    if (
      code_original >= utf_code.zenkaku_numeric_start &&
      code_original <= utf_code.zenkaku_numeric_end
    ) {
      result += String.fromCharCode(code_original - utf_code.zenkaku_numeric_convert);
    } else {
      result += original.charAt(i);
    }
  }

  return result;
};

/**
 * 数値に変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
export const convertToNumeric = (original: string): string => {
  let result = original;

  // 全角数字→半角数字
  result = convertFullNumericToHalfNumeric(result);
  // 数字(0～9)以外を除去して
  result = result.replace(/[^\d]/g, '');

  return result;
};

/**
 * 数値カンマ付与
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertToAddComma = (original: string): string => {
  let result = original;

  // 数値に変換
  result = convertToNumeric(result);

  if (result !== '') {
    // 3桁区切り
    result = new Intl.NumberFormat().format(Number(result));
  }
  return result;
};

/**
 * 全角ドット→半角ドット変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertFullDotToHalfDot = (original: string): string => {
  return original.replace(/．/g, '.');
};

/**
 * 全角英数記号を半角英数記号に変換する
 *
 * @param {string} original 変換対象データ
 * @returns {string} 変換後データ
 */
export const convertToHalf = (original: string): string => {
  return original
    .replace(/[！-～}]/g, function (s) {
      return String.fromCharCode(s.charCodeAt(0) - utf_code.zenkaku_numeric_convert);
    })
    .replace(/[ｰ－―]/g, '-') // ハイフンなど
    .replace(/[～〜]/g, '~') // チルダ
    .replace(/　/g, ' '); // スペース
};

/**
 * 有理数（正と負の小数・分数で表せる数字）の変換
 *
 * @param original 元の値
 * @returns 変換後の値
 */
export const convertToRationalNumber = (original: string): string => {
  let result = original;

  // 全角数字→半角数字,小数点を半角に,全角英数記号（ハイフン）を半角に
  result = convertFullNumericToHalfNumeric(result);
  result = convertFullDotToHalfDot(result);
  result = convertToHalf(result);

  // 数字(0～9,ドット, ハイフン)以外を除去
  result = result.replace(/[^\d^.-]/g, '');

  return result;
};

/**
 * 小数式変換
 *
 * @param original 元の値
 * @param rounded_position 四捨五入位置
 * @param fixed_position 丸め位置
 * @returns 変換後の値
 */
export const convertToDecimal = (
  original: string,
  rounded_position = NUMBER_DECIMALS,
  fixed_position = NUMBER_DECIMALS_ROUND_ZERO
): string => {
  let result = original;

  // 全角数字,小数点→半角数字,小数点
  result = convertFullNumericToHalfNumeric(result);
  result = convertFullDotToHalfDot(result);

  // 数字(0～9,ドット)以外を除去
  result = result.replace(/[^\d^.]/g, '');
  if (result !== '' && !isNaN(parseInt(result))) {
    // 四捨五入
    result = (Math.round(Number(result) * 1000) / 1000).toFixed(rounded_position);
  }

  // 小数点第3位が0の場合は丸める
  const decimal_place = result.match(/[.]([\d]+)$/);
  // 小数点が第N位まである、かつ末尾桁が0である場合は丸める
  if (
    decimal_place !== null &&
    decimal_place[1].length === rounded_position &&
    decimal_place[1].match(/0$/)
  ) {
    result = Number(result).toFixed(fixed_position);
  }

  return result;
};

/**
 * 小数点を含む値を指定の桁で切り捨てカンマ区切りにする
 * @param original 元の値
 * @param fixed_position 切り捨て位置
 * @returns 返還後の値
 */
export const convertToDecimalComma = (original: string, fixed_position = FIXED_NUMBER): string => {
  let result = original;

  // 全角数字,小数点→半角数字,小数点
  result = convertFullNumericToHalfNumeric(result);
  result = convertFullDotToHalfDot(result);

  // 数字(0～9,ドット)以外を除去
  result = result.replace(/[^\d^.]/g, '');

  let decimal_value; // 整数部
  let double_value; // 小数点以下

  // 小数点が含まれる場合
  if (result.includes('.')) {
    decimal_value = String(result).split('.')[0];
    double_value = String(result).split('.')[1];
    //小数点以下を指定の桁数で切り捨て
    double_value = double_value.slice(0, fixed_position);
  } else {
    decimal_value = String(result);
    double_value = '';
  }
  // 整数部を３桁カンマ区切りにする
  decimal_value = new Intl.NumberFormat().format(Number(decimal_value));
  // 整数部と少数部を文字列結合
  if (double_value !== undefined && double_value !== null && double_value !== '') {
    result = String(decimal_value) + '.' + String(double_value);
  } else {
    result = String(decimal_value);
  }
  if (result === '0') {
    result = '';
  }
  return result;
};

/**
 * 半角ｶﾅ→全角カナ変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertHalfKanaToFullKana = (original: string): string => {
  let result = '';
  const len = original.length;

  const reg_exp_base_dakuon = new RegExp('[' + convert_char.hankaku_kana_dakuon_base + ']');
  const reg_exp_base_handakuon = new RegExp('[' + convert_char.hankaku_kana_handakuon_base + ']');

  for (let i = 0; i < len; i++) {
    const c = original.charAt(i);
    const code_original = c.charCodeAt(0);

    if (code_original >= 0xff61 && code_original <= 0xff9f) {
      // 半角ｶﾅ
      let dakuon_or_handakuon = false;

      if (c.match(reg_exp_base_dakuon) != null) {
        if (i < len - 1) {
          // 次の文字
          const code_next = original.charCodeAt(i + 1);

          if (code_next == utf_code.hankaku_dakuten) {
            // 濁点あり
            dakuon_or_handakuon = true;
            i++;

            const index = convert_char.hankaku_kana_dakuon_base.indexOf(c);
            result += convert_char.zenkaku_kana_dakuon.charAt(index);
          }
        }
      }

      if (!dakuon_or_handakuon) {
        if (c.match(reg_exp_base_handakuon) != null) {
          if (i < len - 1) {
            // 次の文字
            const code_next = original.charCodeAt(i + 1);
            if (code_next == utf_code.hankaku_handakuten) {
              // 半濁点あり
              dakuon_or_handakuon = true;
              i++;

              const index = convert_char.hankaku_kana_handakuon_base.indexOf(c);
              result += convert_char.zenkaku_kana_handakuon.charAt(index);
            }
          }
        }
      }

      if (!dakuon_or_handakuon) {
        const index = convert_char.hankaku_kana.indexOf(c);
        result += convert_char.zenkaku_kana.charAt(index);
      }
    } else if (c.match(convert_char.hankaku_space)) {
      //半角スペースの場合全角に変換
      result += convert_char.zenkaku_space;
    } else {
      result += c;
    }
  }

  return result;
};

/**
 * 全角ひらがな→全角カナ変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertHiraganaToFullKana = (original: string): string => {
  let result = '';

  for (let i = 0; i < original.length; i++) {
    const code_original = original.charCodeAt(i);

    if (
      code_original >= utf_code.zenkaku_hiragana_start &&
      code_original <= utf_code.zenkaku_hiragana_end
    ) {
      // 全角ひらがな
      result += String.fromCharCode(code_original + utf_code.zenkaku_hiragana_convert);
    } else {
      result += original.charAt(i);
    }
  }

  return result;
};

/**
 * 全角カナ変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertToFullKana = (original: string): string => {
  return convertHalfKanaToFullKana(convertHiraganaToFullKana(original));
};

/**
 * 全角カナ→半角ｶﾅ変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertFullKanaToHalfKana = (original: string): string => {
  let result = original;

  // 濁点、半濁点なしの文字を置換
  for (let i = 0; i < convert_char.zenkaku_kana.length; i++) {
    const reg_exp = new RegExp(convert_char.zenkaku_kana.charAt(i), 'g');
    result = result.replace(reg_exp, convert_char.hankaku_kana.charAt(i));
  }

  // 濁点ありの文字を置換
  for (let i = 0; i < convert_char.zenkaku_kana_dakuon.length; i++) {
    const reg_exp = new RegExp(convert_char.zenkaku_kana_dakuon.charAt(i), 'g');
    result = result.replace(
      reg_exp,
      convert_char.hankaku_kana_dakuon_base.charAt(i) + convert_char.hankaku_kana_dakuten
    );
  }

  // 半濁点ありの文字を置換
  for (let i = 0; i < convert_char.zenkaku_kana_handakuon.length; i++) {
    const reg_exp = new RegExp(convert_char.zenkaku_kana_handakuon.charAt(i), 'g');
    result = result.replace(
      reg_exp,
      convert_char.hankaku_kana_handakuon_base.charAt(i) + convert_char.hankaku_kana_handakuten
    );
  }

  return result;
};

/**
 * 全角ひらがな→半角ｶﾅ変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertHiraganaToHalfKana = (original: string): string => {
  let result = original;

  // 濁点、半濁点なしの文字を置換
  for (let i = 0; i < convert_char.zenkaku_hiragana.length; i++) {
    const reg_exp = new RegExp(convert_char.zenkaku_hiragana.charAt(i), 'g');
    result = result.replace(reg_exp, convert_char.hankaku_kana.charAt(i));
  }

  // 濁点ありの文字を置換
  for (let i = 0; i < convert_char.zenkaku_hiragana_dakuon.length; i++) {
    const reg_exp = new RegExp(convert_char.zenkaku_hiragana_dakuon.charAt(i), 'g');
    result = result.replace(
      reg_exp,
      convert_char.hankaku_kana_dakuon_base.charAt(i) + convert_char.hankaku_kana_dakuten
    );
  }

  // 半濁点ありの文字を置換
  for (let i = 0; i < convert_char.zenkaku_hiragana_handakuon.length; i++) {
    const reg_exp = new RegExp(convert_char.zenkaku_hiragana_handakuon.charAt(i), 'g');
    result = result.replace(
      reg_exp,
      convert_char.hankaku_kana_handakuon_base.charAt(i) + convert_char.hankaku_kana_handakuten
    );
  }

  return result;
};

/**
 * 半角ｶﾅ変換
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
const convertToHalfKana = (original: string): string => {
  return convertFullKanaToHalfKana(convertHiraganaToHalfKana(original));
};

/**
 * 半角→全角変換
 * @param original 入力値
 * @return string 変換後の文字列
 */
export const convertHalfToFull = (original: string): string => {
  // 半角カタカナを全角カタカナに変換する
  const new_str = convertHalfKanaToFullKana(original);
  // 半角英数字と記号を全角に変換する
  return new_str.replace(/[!-~]/g, (str) => String.fromCharCode(str.charCodeAt(0) + 0xfee0));
};

/**
 * 対象文字を口座名義(半角カナ)の使用文字に変換する
 *
 * @param {string} original 入力値
 * @return {string} 変換後の文字列
 */
export const changeAccountHolderHankakuKana = (original: string): string => {
  return convertToHalf(convertToHalfKana(original)).toUpperCase();
};

/**
 * データ変換
 * @param convert_type 変換タイプ
 * @param value 変換するデータ
 * @return string  変換後のデータ
 */
export const convertData = (convert_type: string, value: string): string => {
  if (convert_type === convert_types.add_comma) {
    return convertToAddComma(value);
  } else if (convert_type === convert_types.change_numeric) {
    return convertToNumeric(value);
  } else if (convert_type === convert_types.change_rational_number) {
    return convertToRationalNumber(value);
  } else if (convert_type === convert_types.add_decimal_point) {
    return convertToDecimal(value);
  } else if (convert_type === convert_types.add_decimal_point_comma) {
    return convertToDecimalComma(value);
  } else if (convert_type === convert_types.change_zenkaku_kana) {
    return convertToFullKana(value);
  } else if (convert_type === convert_types.change_hankaku_kana) {
    return convertToHalfKana(value);
  } else if (convert_type === convert_types.change_zenkaku) {
    return convertHalfToFull(value);
  } else if (convert_type === convert_types.change_account_holder_hankaku_kana) {
    return changeAccountHolderHankakuKana(value);
  } else {
    return value;
  }
};

/**
 * 改行コードをエスケープされていない形に変換する。
 * 数値型は文字列に変換して返却する
 *
 * @param original 変換するデータ
 * @return string  変換後のデータ
 */
export const convertLine = (original: string | number): string => {
  // 数値型の場合はstring化して返却
  if (typeof original === 'number') {
    return String(original);
  } else if (typeof original !== 'string') {
    return original;
  }
  //改行コードがエスケープされている場合はエスケープなしに置換
  return original.match(/\\n/g) !== null ? original.replace(/\\n/g, '\n') : original;
};

/**
 * 配列をハイフン区切りの文字列に変換する
 *
 * @param value 入力値
 * @return string 変換後の値
 */
export const convertArrayJoinHyphen = (value: string[]): string => {
  return value.reduce((prev, current) => {
    return prev || current ? `${prev}-${current}` : '';
  });
};
